import { useEffect, useState } from "react";
import { storyblokEditable, useStoryblokApi } from "@storyblok/react";
import {
  ChevronDown,
  Menu as MenuIcon,
  X as CloseIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

// Cache the datasource entries in memory
let MAIN_MENU_CACHE = null;
let MAIN_MENU_PROMISE = null;

async function getMainMenuEntries(storyblokApi) {
  if (MAIN_MENU_CACHE) return MAIN_MENU_CACHE;

  if (!MAIN_MENU_PROMISE) {
    MAIN_MENU_PROMISE = storyblokApi
      .get("cdn/datasource_entries", {
        datasource: "main-menu",
        version: "published",
        per_page: 100,
      })
      .then(({ data }) => {
        MAIN_MENU_CACHE = data.datasource_entries || [];
        return MAIN_MENU_CACHE;
      })
      .catch((err) => {
        MAIN_MENU_CACHE = null;
        throw err;
      })
      .finally(() => {
        MAIN_MENU_PROMISE = null;
      });
  }

  return MAIN_MENU_PROMISE;
}

const isInternalPath = (href) => {
  if (!href) return false;

  // "/" is internal. "//" and "http(s)://" is external
  return href.startsWith("/") && !href.startsWith("//");
};

const SmartLink = ({
  href,
  className,
  children,
  onClick,
  ...rest
}) => {
  const safeHref =
    typeof href === "string" ? href.trim() : href;

  if (!safeHref) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        {...rest}
      >
        {children}
      </button>
    );
  }

  if (isInternalPath(safeHref)) {
    return (
      <Link
        to={safeHref}
        className={className}
        onClick={onClick}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  // External URLs, tel:, mailto:, etc.
  return (
    <a
      href={safeHref}
      className={className}
      onClick={onClick}
      {...rest}
    >
      {children}
    </a>
  );
};

const Header = ({ blok }) => {

  const radarLogo = blok?.radar_logo?.filename || "";
  const radarLogoAlt = "BlackBerry Radar";

  const storyblokApi = useStoryblokApi();

  const [loadedLogoUrl, setLoadedLogoUrl] = useState("");

  const isLogoLoaded =
    Boolean(radarLogo) && loadedLogoUrl === radarLogo;

  const [platformItems, setPlatformItems] = useState([]);
  const [solutionsItems, setSolutionsItems] = useState([]);

  const [devicesItem, setDevicesItem] = useState(null);
  const [resourcesItem, setResourcesItem] = useState(null);
  const [contactItem, setContactItem] = useState(null);

  const [supportItem, setSupportItem] = useState(null);
  const [phoneItem, setPhoneItem] = useState(null);

  const [isPlatformOpen, setIsPlatformOpen] =
    useState(false);

  const [isSolutionsOpen, setIsSolutionsOpen] =
    useState(false);

  const [isMobileOpen, setIsMobileOpen] =
    useState(false);

  const [isMobilePlatformOpen, setIsMobilePlatformOpen] =
    useState(false);

  const [
    isMobileSolutionsOpen,
    setIsMobileSolutionsOpen,
  ] = useState(false);

  const [menuReady, setMenuReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchMenu = async () => {
      try {
        const entries =
          await getMainMenuEntries(storyblokApi);

        if (!isMounted) return;

        const findByName = (name) =>
          entries.find(
            (item) =>
              (item.name || "").trim().toLowerCase() ===
              name.toLowerCase()
          );

        const findByNames = (...names) => {
          for (const name of names) {
            const match = findByName(name);

            if (match) {
              return match;
            }
          }

          return null;
        };

// Platform dropdown order
        const platform = [
          findByNames(
            "Asset Intelligence",
            "Asset Intelligence (New)"
          ),
          findByName("Portal Login"),
          findByNames(
            "Reporting & Alerts",
            "Dashboards & Reports"
          ),
        ].filter(Boolean);

// Solutions dropdown order
        const solutionsOrder = [
          "Chassis and Flatbed Tracking",
          "Container Tracking",
          "Trailer Tracking",
          "Hazardous Materials",
          "Railcar Tracking and Monitoring Solutions",
          "Equipment Tracking",
        ];

        const solutions = solutionsOrder
          .map((name) => findByName(name))
          .filter(Boolean);

        setPlatformItems(platform);
        setSolutionsItems(solutions);

// Main navigation links
        setDevicesItem(findByName("Devices"));

        setResourcesItem(
          findByNames("Resources", "News & Resources")
        );

        setContactItem(
          findByNames("Contact", "Contact Us")
        );

// Right side nav links
        setSupportItem(findByName("Support"));

        setPhoneItem(
          findByName("Phone") ||
            findByName("Phone Number") ||
            findByName("1-844-239-4572")
        );

        setMenuReady(true);
      } catch (err) {
        console.error("Error fetching menu:", err);

        if (isMounted) {
          setMenuReady(true);
        }
      }
    };

    fetchMenu();

    return () => {
      isMounted = false;
    };
  }, [storyblokApi]);

  const hrefOr = (item, fallback) =>
    (item && item.value) || fallback;

  const labelOr = (item, fallback) =>
    (item && item.name) || fallback;

  const closeMobile = () => {
    setIsMobileOpen(false);
    setIsMobilePlatformOpen(false);
    setIsMobileSolutionsOpen(false);
  };

  return (
    <header
      {...storyblokEditable(blok)}
      className="w-full relative z-50"
    >
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-5 py-3 flex items-center text-sm">
          {/* Logo and left-side navigation */}
          <div className="flex items-center space-x-5">
            {/*
             * Fixed dimensions for logo space before image finishes loading
             */}
            <div className="relative h-[30px] w-[140px] shrink-0">
              {radarLogo ? (
                <SmartLink
                  href="/"
                  className="absolute inset-0 inline-flex items-center"
                  aria-label={radarLogoAlt}
                >
                  <img
                    key={radarLogo}
                    src={radarLogo}
                    alt={radarLogoAlt}
                    className={`h-[30px] w-[140px] object-contain transition-opacity duration-300 ${
                      isLogoLoaded
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    onLoad={() =>
                      setLoadedLogoUrl(radarLogo)
                    }
                  />
                </SmartLink>
              ) : (
                <SmartLink
                  href="/"
                  className="absolute inset-0 inline-flex items-center font-medium text-black hover:text-[#1371DC] transition-colors"
                >
                  BlackBerry Radar
                </SmartLink>
              )}
            </div>

            {/* Desktop navigation */}
            <div className="hidden min-[1045px]:flex items-center space-x-5">
              {/* Platform dropdown */}
              <div
                className="relative inline-block"
                onMouseEnter={() =>
                  setIsPlatformOpen(true)
                }
                onMouseLeave={() =>
                  setIsPlatformOpen(false)
                }
              >
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-black hover:text-[#1371DC] hover:bg-gray-100 transition-colors ${
                    !menuReady ? "opacity-90" : ""
                  }`}
                  aria-haspopup="menu"
                  aria-expanded={isPlatformOpen}
                >
                  <span>Platform</span>

                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isPlatformOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isPlatformOpen &&
                  platformItems.length > 0 && (
                    <div className="absolute left-0 top-full w-64 z-50">
                      <div className="mt-1 bg-white text-black rounded shadow-lg py-2">
                        {platformItems.map((item) => (
                          <SmartLink
                            key={item.id}
                            href={item.value}
                            className="block px-4 py-2 text-sm hover:bg-gray-100"
                          >
                            {item.name}
                          </SmartLink>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              {/* Solutions dropdown */}
              <div
                className="relative inline-block"
                onMouseEnter={() =>
                  setIsSolutionsOpen(true)
                }
                onMouseLeave={() =>
                  setIsSolutionsOpen(false)
                }
              >
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-black hover:text-[#1371DC] hover:bg-gray-100 transition-colors ${
                    !menuReady ? "opacity-90" : ""
                  }`}
                  aria-haspopup="menu"
                  aria-expanded={isSolutionsOpen}
                >
                  <span>Solutions</span>

                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isSolutionsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isSolutionsOpen &&
                  solutionsItems.length > 0 && (
                    <div className="absolute left-0 top-full w-80 z-50">
                      <div className="mt-1 bg-white text-black rounded shadow-lg py-2">
                        {solutionsItems.map((solution) => (
                          <SmartLink
                            key={solution.id}
                            href={solution.value}
                            className="block px-4 py-2 text-sm hover:bg-gray-100"
                          >
                            {solution.name}
                          </SmartLink>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              {/* Devices */}
              <SmartLink
                href={hrefOr(devicesItem, "/devices")}
                className="text-black hover:text-[#1371DC] transition-colors"
              >
                {labelOr(devicesItem, "Devices")}
              </SmartLink>

              {/* Resources */}
              <SmartLink
                href={hrefOr(
                  resourcesItem,
                  "/news-resources"
                )}
                className="text-black hover:text-[#1371DC] transition-colors"
              >
                {labelOr(resourcesItem, "Resources")}
              </SmartLink>

              {/* Contact */}
              <SmartLink
                href={hrefOr(contactItem, "/contact-us")}
                className="text-black hover:text-[#1371DC] transition-colors"
              >
                {labelOr(contactItem, "Contact")}
              </SmartLink>
            </div>
          </div>

          {/* Desktop right-side navigation */}
          <div className="hidden min-[1045px]:flex items-center space-x-5 ml-auto">
            <SmartLink
              href={hrefOr(
                phoneItem,
                "tel:1-844-239-4572"
              )}
              className="text-black hover:text-[#1371DC] transition-colors whitespace-nowrap"
            >
              {labelOr(phoneItem, "1-844-239-4572")}
            </SmartLink>

            <SmartLink
              href={hrefOr(supportItem, "/support")}
              className="text-black hover:text-[#1371DC] transition-colors"
            >
              {labelOr(supportItem, "Support")}
            </SmartLink>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="min-[1045px]:hidden inline-flex items-center justify-center p-2 rounded text-black hover:bg-gray-100 ml-auto"
            onClick={() =>
              setIsMobileOpen((previous) => !previous)
            }
            aria-label="Toggle Radar menu"
            aria-expanded={isMobileOpen}
          >
            {isMobileOpen ? (
              <CloseIcon className="w-6 h-6" />
            ) : (
              <MenuIcon className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile navigation panel */}
        <div
          className={`min-[1045px]:hidden bg-white border-t border-gray-200 transition-[max-height] duration-300 overflow-hidden ${
            isMobileOpen
              ? "max-h-[800px]"
              : "max-h-0"
          }`}
        >
          <div className="px-6 py-4 space-y-4 text-sm">
            {/* Mobile Platform dropdown */}
            <div>
              <button
                type="button"
                className="w-full flex items-center justify-between text-black hover:text-[#1371DC]"
                onClick={() =>
                  setIsMobilePlatformOpen(
                    (previous) => !previous
                  )
                }
                aria-expanded={isMobilePlatformOpen}
              >
                <span>Platform</span>

                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isMobilePlatformOpen
                      ? "rotate-180"
                      : ""
                  }`}
                />
              </button>

              {isMobilePlatformOpen &&
                platformItems.length > 0 && (
                  <div className="mt-2 pl-4 space-y-1">
                    {platformItems.map((item) => (
                      <SmartLink
                        key={item.id}
                        href={item.value}
                        className="block text-sm text-black hover:text-[#1371DC]"
                        onClick={closeMobile}
                      >
                        {item.name}
                      </SmartLink>
                    ))}
                  </div>
                )}
            </div>

            {/* Mobile Solutions dropdown */}
            <div>
              <button
                type="button"
                className="w-full flex items-center justify-between text-black hover:text-[#1371DC]"
                onClick={() =>
                  setIsMobileSolutionsOpen(
                    (previous) => !previous
                  )
                }
                aria-expanded={isMobileSolutionsOpen}
              >
                <span>Solutions</span>

                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isMobileSolutionsOpen
                      ? "rotate-180"
                      : ""
                  }`}
                />
              </button>

              {isMobileSolutionsOpen &&
                solutionsItems.length > 0 && (
                  <div className="mt-2 pl-4 space-y-1">
                    {solutionsItems.map((solution) => (
                      <SmartLink
                        key={solution.id}
                        href={solution.value}
                        className="block text-sm text-black hover:text-[#1371DC]"
                        onClick={closeMobile}
                      >
                        {solution.name}
                      </SmartLink>
                    ))}
                  </div>
                )}
            </div>

            {/* Mobile main navigation links */}
            <div className="flex flex-col space-y-2">
              <SmartLink
                href={hrefOr(devicesItem, "/devices")}
                className="text-black hover:text-[#1371DC]"
                onClick={closeMobile}
              >
                {labelOr(devicesItem, "Devices")}
              </SmartLink>

              <SmartLink
                href={hrefOr(
                  resourcesItem,
                  "/news-resources"
                )}
                className="text-black hover:text-[#1371DC]"
                onClick={closeMobile}
              >
                {labelOr(resourcesItem, "Resources")}
              </SmartLink>

              <SmartLink
                href={hrefOr(contactItem, "/contact-us")}
                className="text-black hover:text-[#1371DC]"
                onClick={closeMobile}
              >
                {labelOr(contactItem, "Contact")}
              </SmartLink>
            </div>

            {/* Mobile secondary navigation links */}
            <div className="border-t border-gray-200 pt-4 flex flex-col space-y-2">
              <SmartLink
                href={hrefOr(
                  phoneItem,
                  "tel:1-844-239-4572"
                )}
                className="text-black hover:text-[#1371DC]"
                onClick={closeMobile}
              >
                {labelOr(
                  phoneItem,
                  "1-844-239-4572"
                )}
              </SmartLink>

              <SmartLink
                href={hrefOr(supportItem, "/support")}
                className="text-black hover:text-[#1371DC]"
                onClick={closeMobile}
              >
                {labelOr(supportItem, "Support")}
              </SmartLink>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;